import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { RefObject } from 'react';
import type { UIContext, UIContextState, UIContextActions } from '@/app/store';
import { useUIStore } from '@/app/store/ui';
import { extractVisibleOutline } from '@/shared/utils/extractWidgetOutline';
import { logger } from '@/lib/utils/logger';

const initialState: UIContextState = {
  context: null,
  widgetRef: null,
};

/**
 * UI Context Store
 *
 * Manages the current UI context that describes what the user is viewing.
 * Widgets use this store (via the useUIContext hook) to set their context,
 * and the chat store reads from it when sending messages.
 */
export const useUIContextStore = create<UIContextState & UIContextActions>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setContext: (context: UIContext) => {
        set({ context });
      },

      setWidgetRef: (ref: RefObject<HTMLElement | null>) => {
        set({ widgetRef: ref });
      },

      clearContext: () => {
        set({ context: null, widgetRef: null });
      },

      getContextSnapshot: (): UIContext | undefined => {
        // Don't return UI context if side panel is closed
        const sidePanelLayoutMode = useUIStore.getState().sidePanelLayoutMode;
        if (sidePanelLayoutMode === null) {
          return undefined;
        }

        const state = get();

        // If we have an explicit context, return it
        if (state.context) {
          return state.context;
        }

        // Fallback: describe the widget's visible content from its ref
        if (state.widgetRef?.current) {
          try {
            const outline = extractVisibleOutline(state.widgetRef.current);
            if (outline) {
              return {
                description: `The user is viewing this content (plain-text outline of the visible panel, indented by nesting):\n${outline}`,
                metadata: {
                  source: 'dom-outline',
                },
              };
            }
          } catch (error) {
            logger.warn('[UIContextStore] Failed to describe widget content:', error);
          }
        }
        return undefined;
      },
    }),
    { name: 'ui-context-store' },
  ),
);
